/**
 * Real Estate Listing — HALCYON Atelier (Editorial Architecture redesign)
 * Slow-luxury property catalog. Classical Cormorant Garamond serif,
 * magazine spreads, Roman numerals, refined underline links.
 * Distinct from VANTA Athletic's numbered editorial monochrome.
 */

export const REAL_ESTATE_LISTING_FILES = {
  'src/index.css': `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Manrope:wght@300;400;500;600&display=swap');

* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body {
  margin: 0;
  font-family: 'Manrope', system-ui, sans-serif;
  background: #0F0D0B;
  color: #E9DFC5;
  -webkit-font-smoothing: antialiased;
  font-weight: 400;
  font-feature-settings: 'ss01';
}

.hl-serif { font-family: 'Cormorant Garamond', 'Garamond', serif; font-weight: 400; letter-spacing: -0.012em; }
.hl-serif-italic { font-family: 'Cormorant Garamond', 'Garamond', serif; font-style: italic; font-weight: 400; }
.hl-sans { font-family: 'Manrope', system-ui, sans-serif; }

@keyframes hl-fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes hl-drift { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }

.hl-eyebrow {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 0.02em;
  color: #C2B698;
}

.hl-roman {
  font-family: 'Cormorant Garamond', serif;
  font-weight: 300;
  letter-spacing: 0.02em;
  font-feature-settings: 'lnum';
}

.hl-rule {
  display: inline-block;
  width: 26px;
  height: 1px;
  background: rgba(233, 199, 122, 0.55);
  vertical-align: middle;
}

.hl-divider { height: 1px; background: rgba(233, 199, 122, 0.12); }
.hl-divider-dark { height: 1px; background: rgba(26, 24, 22, 0.14); }

/* Refined link — primary CTA pattern */
.hl-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-size: 18px;
  font-weight: 400;
  color: #E9C77A;
  cursor: pointer;
  background: transparent;
  border: none;
  transition: opacity 0.4s ease;
}
.hl-link::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 1px;
  background: currentColor;
  transform-origin: left;
  transition: transform 0.5s cubic-bezier(.6,.05,.32,1);
}
.hl-link:hover { opacity: 0.78; }
.hl-link:hover::after { transform: scaleX(0.6); }

.hl-link-cream { color: #1A1816; }

/* Subtle outlined button — secondary action */
.hl-quiet {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 28px;
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.14em;
  color: #E9DFC5;
  background: transparent;
  border: 1px solid rgba(233, 223, 197, 0.18);
  cursor: pointer;
  transition: all 0.5s ease;
}
.hl-quiet:hover { border-color: rgba(233, 199, 122, 0.6); color: #E9C77A; }
.hl-quiet-dark { color: #1A1816; border: 1px solid rgba(26, 24, 22, 0.2); }
.hl-quiet-dark:hover { border-color: rgba(26, 24, 22, 0.55); color: #6B4F1E; }

/* Atmospheric image surfaces */
.hl-atmo {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 65% 30%, rgba(233, 199, 122, 0.18) 0%, transparent 45%),
    radial-gradient(ellipse at 30% 85%, rgba(233, 199, 122, 0.10) 0%, transparent 50%),
    linear-gradient(180deg, #1F1B16 0%, #0E0C0A 100%);
}
.hl-atmo-sunset {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 78% 22%, rgba(255, 207, 130, 0.32) 0%, transparent 38%),
    radial-gradient(circle at 22% 78%, rgba(120, 80, 40, 0.18) 0%, transparent 42%),
    linear-gradient(170deg, #2A211A 0%, #0F0C09 100%);
}
.hl-atmo-paper {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 50% 30%, rgba(255, 235, 190, 0.45) 0%, transparent 55%),
    linear-gradient(180deg, #EFE5CB 0%, #DCCEA8 100%);
}
.hl-atmo-portrait {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 50% 35%, rgba(233, 199, 122, 0.20) 0%, transparent 50%),
    linear-gradient(180deg, #221C15 0%, #0E0B08 100%);
}

.hl-vignette::after {
  content: '';
  position: absolute; inset: 0;
  pointer-events: none;
  background: radial-gradient(ellipse at center, transparent 50%, rgba(15, 13, 11, 0.55) 100%);
}

.hl-num-corner {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 14px;
  color: rgba(233, 223, 197, 0.55);
  letter-spacing: 0.05em;
}
`,

  'src/App.js': `import React from 'react';
import './index.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Search from './components/Search';
import Featured from './components/Featured';
import Interiors from './components/Interiors';
import Neighborhoods from './components/Neighborhoods';
import Agents from './components/Agents';
import Testimonials from './components/Testimonials';
import CTABanner from './components/CTABanner';
import Footer from './components/Footer';

export default function App() {
  return (
    <div className="min-h-screen bg-[#0F0D0B] text-[#E9DFC5]">
      <Navbar />
      <main>
        <Hero />
        <Search />
        <Featured />
        <Interiors />
        <Neighborhoods />
        <Agents />
        <Testimonials />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
}
`,

  'src/data/halcyon.js': `export const NAV_LINKS = [
  { label: 'Residences', href: '#residences' },
  { label: 'Interiors', href: '#interiors' },
  { label: 'Markets', href: '#markets' },
  { label: 'Journal', href: '#journal' },
];

export const HERO_META = [
  { label: 'Volume', value: 'XXIV' },
  { label: 'Spring', value: 'MMXXVI' },
  { label: 'Residences', value: '184' },
  { label: 'Markets', value: 'XI' },
];

export const SEARCH_FIELDS = [
  { label: 'City', value: 'All markets' },
  { label: 'Residence', value: 'Any type' },
  { label: 'Price', value: '$2M — $25M' },
  { label: 'Bedrooms', value: 'Three or more' },
];

export const PROPERTIES = [
  {
    roman: 'I',
    name: 'The Marlowe',
    location: 'Sea Cliff, San Francisco',
    architect: 'Studio Verza, 2023',
    price: '$14.8M',
    status: 'Just listed',
    headline: 'An estate above the Pacific.',
    description: 'Five suites of glass and stone perched on the western edge of the city. A library, a hidden terrace, and an infinity pool that meets the horizon. Quiet at all hours of the day.',
    beds: 5, baths: '5.5', sqft: '6,420 sqft', lot: '0.34 acre',
  },
  {
    roman: 'II',
    name: 'Atrio Penthouse',
    location: 'Tribeca, New York',
    architect: 'Renzo & Hale, 2021',
    price: '$22.4M',
    status: 'Off-market',
    headline: 'A full-floor sanctuary above the Hudson.',
    description: 'Cast-bronze details, oak interiors, and a private library. River views on three sides and a terrace that catches the morning over Brooklyn.',
    beds: 4, baths: '4.5', sqft: '5,100 sqft', lot: '— —',
  },
  {
    roman: 'III',
    name: 'Casa Albéra',
    location: 'Es Cubells, Ibiza',
    architect: 'Atelier Brun, 2024',
    price: '€8.2M',
    status: 'Now showing',
    headline: 'A villa carved into the southern cliffs.',
    description: 'Six suites, an olive grove, a sea-water spa, and a long flat roof for watching the sun fall into the Mediterranean. Built slowly. Lived in slowly.',
    beds: 6, baths: '6', sqft: '7,800 sqft', lot: '1.2 acres',
  },
  {
    roman: 'IV',
    name: 'Loft Quinze',
    location: 'Le Marais, Paris',
    architect: 'Maison K, 2020',
    price: '€4.6M',
    status: 'Available',
    headline: 'A 19th-century atelier turned residence.',
    description: 'Original steel beams overhead, raw oak underfoot. A private courtyard garden hidden behind a porte-cochère, two minutes from the Place des Vosges.',
    beds: 3, baths: '3', sqft: '3,420 sqft', lot: '— —',
  },
];

export const INTERIORS = [
  { roman: 'i', label: 'Sea-glass living room', residence: 'The Marlowe · Sea Cliff', mood: 'Linen · oak · raw plaster', size: 'wide' },
  { roman: 'ii', label: 'Marble kitchen', residence: 'Atrio · Tribeca', mood: 'Honed marble · custom millwork', size: 'tall' },
  { roman: 'iii', label: 'Travertine bath', residence: 'The Marlowe · Sea Cliff', mood: 'Travertine wet room · brass', size: 'square' },
  { roman: 'iv', label: 'Reading library', residence: 'Atrio · Tribeca', mood: 'Walnut shelving · cast bronze', size: 'square' },
];

export const NEIGHBORHOODS = [
  { chapter: 'Chapter One', city: 'New York', area: 'Tribeca · West Village', listings: 38, avg: '$11.2M', detail: 'Cast-iron facades, river views at sunset, and the quietest restaurants in the city. The neighborhood for clients who keep their lives private and their tables small.' },
  { chapter: 'Chapter Two', city: 'San Francisco', area: 'Pacific Heights · Sea Cliff', listings: 24, avg: '$9.6M', detail: 'Cliffside light, Pacific air, the longest sightlines in the city. Houses that lean toward the ocean and gardens that survive the wind.' },
  { chapter: 'Chapter Three', city: 'Ibiza', area: 'Es Cubells · Cala Jondal', listings: 18, avg: '€6.8M', detail: 'Wind-shaped pines, terraced architecture, and water so clear the boats appear to hover. A market for second homes that become first ones.' },
  { chapter: 'Chapter Four', city: 'Paris', area: 'Le Marais · Saint-Germain', listings: 22, avg: '€5.4M', detail: 'Hotel particulier doorways, hidden courtyards, and plaster that has held its color since the eighteenth century. The slowest, most deliberate market we represent.' },
];

export const AGENTS = [
  { roman: 'I', name: 'Inès Bellamy', role: 'Principal · Paris', sold: '€118M', years: 14, signature: 'European estates · Saint-Germain · 7e', note: 'A former preservation architect. Bilingual French and English; reads Italian.' },
  { roman: 'II', name: 'Marcus Aldrich', role: 'Partner · New York', sold: '$240M', years: 17, signature: 'Penthouse & townhouse · Manhattan · Brooklyn', note: 'Twelve years at a global brokerage before founding HALCYON New York with Inès in 2018.' },
  { roman: 'III', name: 'Sora Akiyama', role: 'Director · Tokyo + LA', sold: '$182M', years: 11, signature: 'Architectural residences · West Coast · Japan', note: 'Trained as an interior architect at Cooper Union. Works between Los Angeles and Tokyo.' },
];

export const TESTIMONIALS = [
  { quote: 'They sold our cliff house in eleven days at full ask. The campaign felt like an Architectural Digest spread we got to live in for a week.', name: 'L. & M. Okafor', meta: 'Seller · Sea Cliff, 2024' },
  { quote: 'I have worked with the big global brands. The taste, the discretion, and the depth of off-market access is unmatched.', name: 'D. Marin', meta: 'Buyer · Tribeca, 2025' },
];

export const FOOTER_COLS = [
  { title: 'Atelier', links: ['Residences', 'Estates', 'Penthouses', 'Off-market'] },
  { title: 'Markets', links: ['New York', 'San Francisco', 'Paris', 'Ibiza', 'Tokyo'] },
  { title: 'Studio', links: ['About', 'Journal', 'Press', 'Careers'] },
];

export const FOOTER_OFFICES = [
  { city: 'New York', addr: '14 Greene St · SoHo' },
  { city: 'Paris', addr: '6 Rue de Bretagne · 3e' },
  { city: 'Ibiza', addr: 'Carrer dels Pintors · Eivissa' },
];
`,

  'src/components/Atmosphere.js': `import React from 'react';

/** Subtle atmospheric placeholders — feel like a cinematic interior photograph at golden hour. */
export function ResidenceArt({ variant = 1 }) {
  if (variant === 1) {
    return (
      <svg viewBox="0 0 800 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        <defs>
          <radialGradient id="sun1" cx="72%" cy="28%" r="44%">
            <stop offset="0%" stopColor="rgba(255,215,150,0.65)" />
            <stop offset="60%" stopColor="rgba(233,199,122,0.18)" />
            <stop offset="100%" stopColor="rgba(15,13,11,0)" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="800" height="540" fill="url(#sun1)" opacity="0.7" />
        <g stroke="rgba(233,199,122,0.22)" fill="none" strokeWidth="0.9">
          <line x1="0" y1="380" x2="800" y2="380" />
          <line x1="0" y1="386" x2="800" y2="386" />
          <path d="M120 380 L120 220 L260 220 L260 160 L420 160 L420 200 L560 200 L560 250 L680 250 L680 380" strokeWidth="1.2" />
          <line x1="180" y1="220" x2="180" y2="380" />
          <line x1="220" y1="220" x2="220" y2="380" />
          <line x1="320" y1="160" x2="320" y2="380" />
          <line x1="380" y1="160" x2="380" y2="380" />
          <line x1="460" y1="200" x2="460" y2="380" />
          <line x1="510" y1="200" x2="510" y2="380" />
          <line x1="600" y1="250" x2="600" y2="380" />
          <line x1="640" y1="250" x2="640" y2="380" />
        </g>
      </svg>
    );
  }
  if (variant === 2) {
    return (
      <svg viewBox="0 0 800 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        <defs>
          <radialGradient id="sun2" cx="30%" cy="38%" r="50%">
            <stop offset="0%" stopColor="rgba(255,210,140,0.6)" />
            <stop offset="60%" stopColor="rgba(233,199,122,0.15)" />
            <stop offset="100%" stopColor="rgba(15,13,11,0)" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="800" height="540" fill="url(#sun2)" />
        <g stroke="rgba(233,199,122,0.24)" fill="none" strokeWidth="0.9">
          <line x1="0" y1="420" x2="800" y2="420" />
          <rect x="60" y="120" width="160" height="300" />
          <rect x="240" y="80" width="200" height="340" />
          <rect x="460" y="160" width="140" height="260" />
          <rect x="620" y="120" width="120" height="300" />
          <g strokeWidth="0.4">
            <line x1="60" y1="180" x2="220" y2="180" />
            <line x1="60" y1="240" x2="220" y2="240" />
            <line x1="60" y1="300" x2="220" y2="300" />
            <line x1="60" y1="360" x2="220" y2="360" />
            <line x1="240" y1="160" x2="440" y2="160" />
            <line x1="240" y1="240" x2="440" y2="240" />
            <line x1="240" y1="320" x2="440" y2="320" />
            <line x1="460" y1="220" x2="600" y2="220" />
            <line x1="460" y1="300" x2="600" y2="300" />
            <line x1="620" y1="180" x2="740" y2="180" />
            <line x1="620" y1="260" x2="740" y2="260" />
            <line x1="620" y1="340" x2="740" y2="340" />
          </g>
        </g>
      </svg>
    );
  }
  if (variant === 3) {
    return (
      <svg viewBox="0 0 800 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        <defs>
          <radialGradient id="sun3" cx="80%" cy="20%" r="50%">
            <stop offset="0%" stopColor="rgba(255,225,160,0.55)" />
            <stop offset="100%" stopColor="rgba(15,13,11,0)" />
          </radialGradient>
        </defs>
        <rect width="800" height="540" fill="url(#sun3)" />
        <g stroke="rgba(233,199,122,0.22)" fill="none" strokeWidth="0.9">
          <path d="M0 360 L130 360 L130 240 L220 240 L220 180 L340 180 L340 210 L520 210 L520 160 L640 160 L640 240 L800 240 L800 360" strokeWidth="1.2" />
          <line x1="0" y1="430" x2="800" y2="430" />
          <line x1="0" y1="436" x2="800" y2="436" />
          <circle cx="640" cy="100" r="42" fill="rgba(233,199,122,0.18)" stroke="none" />
        </g>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 800 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
      <defs>
        <radialGradient id="sun4" cx="50%" cy="40%" r="55%">
          <stop offset="0%" stopColor="rgba(255,215,150,0.55)" />
          <stop offset="100%" stopColor="rgba(15,13,11,0)" />
        </radialGradient>
      </defs>
      <rect width="800" height="540" fill="url(#sun4)" />
      <g stroke="rgba(233,199,122,0.22)" fill="none" strokeWidth="0.9">
        <line x1="0" y1="400" x2="800" y2="400" />
        <rect x="120" y="160" width="560" height="240" />
        <line x1="120" y1="240" x2="680" y2="240" />
        <line x1="120" y1="320" x2="680" y2="320" />
        <line x1="280" y1="160" x2="280" y2="400" />
        <line x1="400" y1="160" x2="400" y2="400" />
        <line x1="520" y1="160" x2="520" y2="400" />
        <line x1="640" y1="160" x2="640" y2="400" />
      </g>
    </svg>
  );
}

export function InteriorArt({ variant = 1, dark = false }) {
  const stroke = dark ? 'rgba(233,199,122,0.22)' : 'rgba(26,24,22,0.28)';
  const fill = dark ? 'rgba(233,199,122,0.04)' : 'rgba(26,24,22,0.04)';
  if (variant === 1) {
    return (
      <svg viewBox="0 0 800 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        <g stroke={stroke} fill="none" strokeWidth="0.9">
          <line x1="0" y1="420" x2="800" y2="420" />
          <rect x="60" y="80" width="320" height="340" fill={fill} />
          <rect x="420" y="80" width="320" height="340" fill={fill} />
          <line x1="60" y1="240" x2="380" y2="240" />
          <line x1="420" y1="240" x2="740" y2="240" />
          <line x1="220" y1="80" x2="220" y2="420" />
          <line x1="580" y1="80" x2="580" y2="420" />
          <rect x="40" y="450" width="720" height="40" />
        </g>
      </svg>
    );
  }
  if (variant === 2) {
    return (
      <svg viewBox="0 0 800 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        <g stroke={stroke} fill="none" strokeWidth="0.9">
          <rect x="80" y="120" width="640" height="40" fill={fill} />
          <rect x="80" y="180" width="640" height="40" fill={fill} />
          <rect x="80" y="240" width="640" height="40" fill={fill} />
          <rect x="80" y="300" width="640" height="40" fill={fill} />
          <rect x="60" y="400" width="680" height="80" fill={fill} />
          <line x1="400" y1="400" x2="400" y2="480" />
        </g>
      </svg>
    );
  }
  if (variant === 3) {
    return (
      <svg viewBox="0 0 800 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
        <g stroke={stroke} fill="none" strokeWidth="0.9">
          <circle cx="400" cy="250" r="140" fill={fill} />
          <line x1="260" y1="250" x2="540" y2="250" />
          <line x1="400" y1="110" x2="400" y2="390" />
          <rect x="80" y="430" width="640" height="40" fill={fill} />
        </g>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 800 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
      <g stroke={stroke} fill="none" strokeWidth="0.9">
        <line x1="0" y1="420" x2="800" y2="420" />
        <line x1="220" y1="80" x2="220" y2="420" />
        <line x1="400" y1="80" x2="400" y2="420" />
        <line x1="580" y1="80" x2="580" y2="420" />
        <rect x="240" y="180" width="120" height="200" fill={fill} />
        <rect x="420" y="180" width="120" height="200" fill={fill} />
      </g>
    </svg>
  );
}

export function PortraitArt() {
  return (
    <svg viewBox="0 0 400 540" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full">
      <defs>
        <radialGradient id="por1" cx="50%" cy="40%" r="55%">
          <stop offset="0%" stopColor="rgba(255,215,150,0.4)" />
          <stop offset="100%" stopColor="rgba(15,13,11,0)" />
        </radialGradient>
      </defs>
      <rect width="400" height="540" fill="url(#por1)" />
      <g stroke="rgba(233,199,122,0.28)" fill="none" strokeWidth="0.9">
        <circle cx="200" cy="200" r="68" />
        <path d="M80 480 C 96 360, 304 360, 320 480" />
        <line x1="0" y1="480" x2="400" y2="480" strokeWidth="0.6" />
      </g>
    </svg>
  );
}

export function ArrowIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round">
      <path d="M5 12h14M14 6l6 6-6 6" />
    </svg>
  );
}
`,

  'src/components/Navbar.js': `import React from 'react';
import { NAV_LINKS } from '../data/halcyon';
import { ArrowIcon } from './Atmosphere';

export default function Navbar() {
  return (
    <header className="sticky top-0 z-40 border-b border-[#E9C77A]/10 bg-[#0F0D0B]/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1320px] items-center justify-between px-8 py-7 md:px-14">
        <a href="#top" className="flex items-baseline gap-3">
          <span className="hl-serif text-[30px] tracking-[0.005em] text-[#E9DFC5]">Halcyon</span>
          <span className="hl-serif-italic text-[14px] text-[#9C9485]">— Atelier</span>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="hl-serif text-[16px] text-[#C2B698] transition hover:text-[#E9C77A]">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          <a href="#client" className="hidden hl-serif-italic text-[15px] text-[#9C9485] transition hover:text-[#E9C77A] md:inline">
            Client portal
          </a>
          <button type="button" className="hl-link">
            Request a viewing
            <ArrowIcon size={13} />
          </button>
        </div>
      </div>
    </header>
  );
}
`,

  'src/components/Hero.js': `import React from 'react';
import { HERO_META } from '../data/halcyon';
import { ResidenceArt } from './Atmosphere';

export default function Hero() {
  return (
    <section id="top" className="relative">
      <div className="mx-auto max-w-[1320px] px-8 pt-20 md:px-14 lg:pt-28">
        <div className="grid grid-cols-12 items-end gap-12">
          <div className="col-span-12 lg:col-span-7">
            <p className="hl-eyebrow flex items-center gap-3">
              <span className="hl-rule" />
              Spring portfolio · Volume <span className="hl-roman">XXIV</span>
            </p>
            <h1 className="hl-serif mt-10 text-[64px] leading-[0.98] text-[#E9DFC5] sm:text-[88px] md:text-[112px]">
              Architecturally <span className="hl-serif-italic text-[#E9C77A]">significant</span>
              <br />
              homes,
              <br />
              <span className="hl-serif-italic">quietly</span> presented.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9">
            <p className="hl-serif text-[20px] leading-[1.6] text-[#C2B698]">
              HALCYON is a private real estate atelier representing a small,
              curated portfolio of significant homes across four global cities.
              We work slowly, with intention, for clients who notice everything.
            </p>
            <p className="hl-serif-italic mt-8 text-[16px] text-[#9C9485]">
              By appointment only.
            </p>
          </div>
        </div>
      </div>

      <figure className="relative mt-20">
        <div className="hl-atmo-sunset relative h-[68vh] min-h-[520px] w-full hl-vignette">
          <ResidenceArt variant={1} />
          <figcaption className="absolute inset-x-0 bottom-0 px-8 pb-14 md:px-14 lg:pb-20">
            <div className="mx-auto max-w-[1320px]">
              <p className="hl-eyebrow">
                <span className="hl-roman">I.</span> Featured estate
              </p>
              <h2 className="hl-serif mt-5 text-[48px] leading-[1.02] text-[#F4ECD4] sm:text-[64px] md:text-[88px]">
                <span className="hl-serif-italic">The Marlowe,</span> Sea Cliff.
              </h2>
              <p className="hl-serif mt-4 text-[19px] text-[#D8CBA4]">
                An estate above the Pacific · Studio Verza, 2023 · Guide $14.8M
              </p>
              <div className="mt-8">
                <button type="button" className="hl-link">View this residence</button>
              </div>
            </div>
          </figcaption>
        </div>
      </figure>

      <div className="mx-auto mt-14 max-w-[1320px] px-8 pb-12 md:px-14">
        <div className="hl-divider" />
        <dl className="mt-8 grid grid-cols-2 gap-y-6 sm:grid-cols-4">
          {HERO_META.map((m, i) => (
            <div key={m.label} className={(i < 3 ? 'sm:border-r sm:border-[#E9C77A]/12 sm:pr-6 ' : '') + 'flex items-baseline gap-4'}>
              <span className="hl-serif-italic text-[14px] text-[#9C9485]">{m.label}</span>
              <span className="hl-serif text-[36px] text-[#E9DFC5] sm:text-[40px]">{m.value}</span>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
`,

  'src/components/Search.js': `import React from 'react';
import { SEARCH_FIELDS } from '../data/halcyon';
import { ArrowIcon } from './Atmosphere';

export default function Search() {
  return (
    <section className="relative border-y border-[#E9C77A]/10 bg-[#15110D]">
      <div className="mx-auto max-w-[1320px] px-8 py-14 md:px-14 lg:py-16">
        <div className="grid grid-cols-12 items-end gap-10">
          <div className="col-span-12 lg:col-span-3">
            <p className="hl-eyebrow"><span className="hl-rule" /> The discovery bar</p>
            <h2 className="hl-serif mt-4 text-[36px] leading-[1.02] text-[#E9DFC5] sm:text-[44px]">
              Find a <span className="hl-serif-italic text-[#E9C77A]">residence.</span>
            </h2>
          </div>

          <div className="col-span-12 lg:col-span-9">
            <div className="grid grid-cols-2 gap-x-10 gap-y-8 sm:grid-cols-4 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] lg:items-end">
              {SEARCH_FIELDS.map((f) => (
                <div key={f.label} className="border-b border-[#E9C77A]/22 pb-4 transition hover:border-[#E9C77A]/60">
                  <p className="hl-serif-italic text-[13px] text-[#9C9485]">{f.label}</p>
                  <div className="mt-2 flex items-baseline justify-between gap-3">
                    <p className="hl-serif text-[22px] text-[#E9DFC5] sm:text-[24px]">{f.value}</p>
                    <svg width="10" height="6" viewBox="0 0 12 8" fill="none" stroke="currentColor" strokeWidth="1.2" className="text-[#9C9485]"><path d="M1 1.5 6 6.5 11 1.5" /></svg>
                  </div>
                </div>
              ))}
              <button type="button" className="hl-link justify-self-end self-end pb-1">
                Discover
                <ArrowIcon size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Featured.js': `import React from 'react';
import { PROPERTIES } from '../data/halcyon';
import { ResidenceArt, ArrowIcon } from './Atmosphere';

export default function Featured() {
  return (
    <section id="residences" className="relative">
      <div className="mx-auto max-w-[1320px] px-8 py-28 md:px-14 lg:py-36">
        <div className="grid grid-cols-12 items-end gap-10">
          <div className="col-span-12 lg:col-span-8">
            <p className="hl-eyebrow"><span className="hl-rule" /> Featured residences</p>
            <h2 className="hl-serif mt-6 text-[52px] leading-[1.02] text-[#E9DFC5] sm:text-[72px] md:text-[96px]">
              A <span className="hl-serif-italic text-[#E9C77A]">private</span> catalog of four.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="hl-serif text-[19px] leading-[1.65] text-[#C2B698]">
              Selected by our directors. Each residence has been visited,
              studied, and approved by the studio before it appears here.
            </p>
            <p className="hl-serif-italic mt-6 text-[15px] text-[#9C9485]">
              Updated every season. Off-market on request.
            </p>
          </div>
        </div>

        <div className="mt-24 flex flex-col gap-24">
          {PROPERTIES.map((p, i) => {
            const reverse = i % 2 === 1;
            return (
              <article key={p.roman} className="grid grid-cols-12 gap-10 items-center">
                <figure className={'col-span-12 lg:col-span-7 ' + (reverse ? 'lg:order-2' : '')}>
                  <div className="hl-atmo-sunset relative aspect-[5/4] w-full overflow-hidden hl-vignette">
                    <ResidenceArt variant={(i % 4) + 1} />
                    <div className="absolute left-7 top-7">
                      <p className="hl-num-corner italic">{p.status}</p>
                    </div>
                  </div>
                </figure>

                <div className={'col-span-12 lg:col-span-5 ' + (reverse ? 'lg:order-1 lg:pr-10' : 'lg:pl-10')}>
                  <p className="hl-eyebrow flex items-baseline gap-3">
                    <span className="hl-roman text-[22px] text-[#E9C77A]">{p.roman}.</span>
                    <span>{p.location}</span>
                  </p>
                  <h3 className="hl-serif mt-5 text-[44px] leading-[1.04] text-[#E9DFC5] sm:text-[56px] md:text-[64px]">
                    <span className="hl-serif-italic">{p.name}.</span>
                  </h3>
                  <p className="hl-serif mt-4 text-[20px] leading-[1.45] text-[#D8CBA4]">{p.headline}</p>
                  <p className="hl-sans mt-6 text-[14.5px] leading-[1.85] text-[#A89E84]">{p.description}</p>

                  <div className="mt-9 flex flex-wrap items-baseline gap-x-8 gap-y-3 border-t border-[#E9C77A]/12 pt-7">
                    <span className="hl-serif-italic text-[13px] text-[#9C9485]">{p.beds} beds</span>
                    <span className="hl-serif-italic text-[13px] text-[#9C9485]">{p.baths} baths</span>
                    <span className="hl-serif-italic text-[13px] text-[#9C9485]">{p.sqft}</span>
                    <span className="hl-serif-italic text-[13px] text-[#9C9485]">{p.lot}</span>
                  </div>

                  <div className="mt-8 flex flex-wrap items-baseline justify-between gap-6">
                    <div>
                      <p className="hl-serif-italic text-[13px] text-[#9C9485]">{p.architect}</p>
                      <p className="hl-serif mt-2 text-[36px] text-[#E9C77A]">{p.price}</p>
                    </div>
                    <button type="button" className="hl-link">
                      View residence
                      <ArrowIcon size={13} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-24 flex justify-center">
          <button type="button" className="hl-quiet">The full atelier · 184 listings</button>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Interiors.js': `import React from 'react';
import { INTERIORS } from '../data/halcyon';
import { InteriorArt, ArrowIcon } from './Atmosphere';

export default function Interiors() {
  return (
    <section id="interiors" className="relative bg-[#EFE5CB] text-[#1A1816]">
      <div className="mx-auto max-w-[1320px] px-8 py-28 md:px-14 lg:py-36">
        <div className="grid grid-cols-12 items-end gap-10">
          <div className="col-span-12 lg:col-span-7">
            <p className="hl-eyebrow text-[#6B4F1E]"><span className="hl-rule bg-[#6B4F1E]/60" /> Interiors</p>
            <h2 className="hl-serif mt-6 text-[52px] leading-[1.02] sm:text-[72px] md:text-[96px]">
              Inside, the <span className="hl-serif-italic text-[#9B6A2D]">light</span> does the work.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:col-start-9">
            <p className="hl-serif text-[19px] leading-[1.65] text-[#3A332B]">
              Quiet materials. Honest proportions.
              Rooms shaped around morning light and a long view.
              Every detail signed off by the principal architect of record.
            </p>
            <p className="hl-serif-italic mt-6 text-[15px] text-[#6B5F4C]">
              Photographed by the studio. No staging.
            </p>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-12 gap-8 lg:gap-10">
          <figure className="col-span-12 lg:col-span-8">
            <div className="hl-atmo-paper relative aspect-[16/10] w-full overflow-hidden">
              <InteriorArt variant={1} dark={false} />
            </div>
            <figcaption className="mt-6 flex items-end justify-between">
              <div>
                <p className="hl-serif-italic text-[14px] text-[#6B5F4C]">{INTERIORS[0].roman}. {INTERIORS[0].label}</p>
                <p className="hl-serif mt-1 text-[24px] text-[#1A1816]">{INTERIORS[0].residence}</p>
              </div>
              <p className="hl-serif-italic text-[13px] text-[#6B5F4C]">{INTERIORS[0].mood}</p>
            </figcaption>
          </figure>

          <figure className="col-span-12 lg:col-span-4 lg:row-span-2">
            <div className="hl-atmo-paper relative aspect-[3/4.4] w-full overflow-hidden">
              <InteriorArt variant={2} dark={false} />
            </div>
            <figcaption className="mt-6">
              <p className="hl-serif-italic text-[14px] text-[#6B5F4C]">{INTERIORS[1].roman}. {INTERIORS[1].label}</p>
              <p className="hl-serif mt-1 text-[24px] text-[#1A1816]">{INTERIORS[1].residence}</p>
              <p className="hl-serif-italic mt-1 text-[13px] text-[#6B5F4C]">{INTERIORS[1].mood}</p>
            </figcaption>
          </figure>

          <figure className="col-span-12 sm:col-span-6 lg:col-span-4">
            <div className="hl-atmo-paper relative aspect-[4/3] w-full overflow-hidden">
              <InteriorArt variant={3} dark={false} />
            </div>
            <figcaption className="mt-5">
              <p className="hl-serif-italic text-[14px] text-[#6B5F4C]">{INTERIORS[2].roman}. {INTERIORS[2].label}</p>
              <p className="hl-serif mt-1 text-[22px] text-[#1A1816]">{INTERIORS[2].residence}</p>
            </figcaption>
          </figure>

          <figure className="col-span-12 sm:col-span-6 lg:col-span-4">
            <div className="hl-atmo-paper relative aspect-[4/3] w-full overflow-hidden">
              <InteriorArt variant={4} dark={false} />
            </div>
            <figcaption className="mt-5">
              <p className="hl-serif-italic text-[14px] text-[#6B5F4C]">{INTERIORS[3].roman}. {INTERIORS[3].label}</p>
              <p className="hl-serif mt-1 text-[22px] text-[#1A1816]">{INTERIORS[3].residence}</p>
            </figcaption>
          </figure>
        </div>

        <div className="mt-16 flex justify-center">
          <button type="button" className="hl-quiet hl-quiet-dark">
            The interiors journal
            <ArrowIcon size={13} />
          </button>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Neighborhoods.js': `import React from 'react';
import { NEIGHBORHOODS } from '../data/halcyon';
import { ResidenceArt, ArrowIcon } from './Atmosphere';

export default function Neighborhoods() {
  return (
    <section id="markets" className="relative">
      <div className="mx-auto max-w-[1320px] px-8 py-28 md:px-14 lg:py-36">
        <div className="grid grid-cols-12 items-end gap-10">
          <div className="col-span-12 lg:col-span-8">
            <p className="hl-eyebrow"><span className="hl-rule" /> Markets</p>
            <h2 className="hl-serif mt-6 text-[52px] leading-[1.02] text-[#E9DFC5] sm:text-[72px] md:text-[96px]">
              Four cities. <span className="hl-serif-italic text-[#E9C77A]">One standard.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="hl-serif text-[19px] leading-[1.65] text-[#C2B698]">
              A small, fluent practice in four global markets.
              We know the streets, the architects, the doormen,
              the directors of the museums.
            </p>
          </div>
        </div>

        <div className="mt-24 flex flex-col gap-28">
          {NEIGHBORHOODS.map((n, i) => {
            const reverse = i % 2 === 1;
            return (
              <article key={n.chapter} className="grid grid-cols-12 items-center gap-10">
                <div className={'col-span-12 lg:col-span-5 ' + (reverse ? 'lg:order-2 lg:pl-10' : 'lg:pr-10')}>
                  <p className="hl-serif-italic text-[15px] text-[#9C9485]">{n.chapter}</p>
                  <h3 className="hl-serif mt-3 text-[60px] leading-[1.02] text-[#E9DFC5] sm:text-[76px] md:text-[88px]">
                    {n.city}.
                  </h3>
                  <p className="hl-serif-italic mt-3 text-[17px] text-[#E9C77A]">{n.area}</p>
                  <p className="hl-sans mt-7 text-[14.5px] leading-[1.85] text-[#A89E84]">{n.detail}</p>

                  <div className="mt-9 flex items-end justify-between border-t border-[#E9C77A]/12 pt-6">
                    <div className="flex items-baseline gap-5">
                      <span className="hl-serif text-[28px] text-[#E9DFC5]">{n.listings}</span>
                      <span className="hl-serif-italic text-[13px] text-[#9C9485]">residences listed</span>
                    </div>
                    <div className="text-right">
                      <p className="hl-serif-italic text-[12px] text-[#9C9485]">Median</p>
                      <p className="hl-serif text-[22px] text-[#E9C77A]">{n.avg}</p>
                    </div>
                  </div>

                  <div className="mt-7">
                    <button type="button" className="hl-link">
                      Read the chapter
                      <ArrowIcon size={13} />
                    </button>
                  </div>
                </div>

                <figure className={'col-span-12 lg:col-span-7 ' + (reverse ? 'lg:order-1' : '')}>
                  <div className="hl-atmo-sunset relative aspect-[5/4] w-full overflow-hidden hl-vignette">
                    <ResidenceArt variant={((i + 1) % 4) + 1} />
                  </div>
                </figure>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Agents.js': `import React from 'react';
import { AGENTS } from '../data/halcyon';
import { PortraitArt, ArrowIcon } from './Atmosphere';

export default function Agents() {
  return (
    <section className="relative border-t border-[#E9C77A]/10 bg-[#13100C]">
      <div className="mx-auto max-w-[1320px] px-8 py-28 md:px-14 lg:py-36">
        <div className="grid grid-cols-12 items-end gap-10">
          <div className="col-span-12 lg:col-span-8">
            <p className="hl-eyebrow"><span className="hl-rule" /> The studio</p>
            <h2 className="hl-serif mt-6 text-[52px] leading-[1.02] text-[#E9DFC5] sm:text-[72px] md:text-[96px]">
              A studio of <span className="hl-serif-italic text-[#E9C77A]">nine directors.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="hl-serif text-[19px] leading-[1.65] text-[#C2B698]">
              No franchises. No managed agents. Every client works directly with a principal
              director who has spent a decade or more in the city you are buying in.
            </p>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-12 gap-x-10 gap-y-16">
          {AGENTS.map((a, i) => (
            <article key={a.roman} className={'col-span-12 sm:col-span-6 lg:col-span-4 ' + (i === 1 ? 'lg:translate-y-12' : '')}>
              <figure className="hl-atmo-portrait relative aspect-[3/4] w-full overflow-hidden hl-vignette">
                <PortraitArt />
                <figcaption className="absolute left-6 top-6">
                  <p className="hl-roman text-[20px] text-[#E9DFC5]/65">{a.roman}.</p>
                </figcaption>
              </figure>
              <div className="mt-7">
                <p className="hl-serif-italic text-[14px] text-[#9C9485]">{a.role}</p>
                <h3 className="hl-serif mt-2 text-[36px] leading-[1.05] text-[#E9DFC5] sm:text-[40px]">{a.name}</h3>
                <p className="hl-serif-italic mt-3 text-[15px] text-[#E9C77A]">{a.signature}</p>
                <p className="hl-sans mt-5 text-[13.5px] leading-[1.8] text-[#A89E84]">{a.note}</p>
              </div>
              <div className="mt-7 flex items-end justify-between border-t border-[#E9C77A]/12 pt-5">
                <div>
                  <p className="hl-serif-italic text-[12px] text-[#9C9485]">Career sold</p>
                  <p className="hl-serif mt-1 text-[26px] text-[#E9DFC5]">{a.sold}</p>
                </div>
                <div className="text-right">
                  <p className="hl-serif-italic text-[12px] text-[#9C9485]">In practice</p>
                  <p className="hl-serif mt-1 text-[26px] text-[#E9DFC5]">{a.years} <span className="hl-serif-italic text-[15px]">yrs</span></p>
                </div>
              </div>
              <button type="button" className="hl-link mt-6">
                Request introduction
                <ArrowIcon size={13} />
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Testimonials.js': `import React from 'react';
import { TESTIMONIALS } from '../data/halcyon';

export default function Testimonials() {
  return (
    <section className="relative">
      <div className="mx-auto max-w-[1320px] px-8 py-28 md:px-14 lg:py-36">
        <p className="hl-eyebrow"><span className="hl-rule" /> Letters from the atelier</p>

        <div className="mt-16 grid grid-cols-12 gap-12">
          {TESTIMONIALS.map((t, i) => (
            <figure key={t.name} className={'col-span-12 lg:col-span-10 ' + (i === 1 ? 'lg:col-start-3' : 'lg:col-start-1')}>
              <p className="hl-serif text-[36px] leading-[1.18] text-[#E9DFC5] sm:text-[46px] md:text-[58px] lg:text-[66px]">
                <span className="hl-serif-italic text-[#E9C77A] text-[58px] leading-none align-top mr-1">&ldquo;</span>
                {t.quote}
                <span className="hl-serif-italic text-[#E9C77A]">&rdquo;</span>
              </p>
              <figcaption className="mt-10 flex items-baseline justify-between border-t border-[#E9C77A]/12 pt-6">
                <div>
                  <p className="hl-serif text-[20px] text-[#E9DFC5]">{t.name}</p>
                  <p className="hl-serif-italic mt-1 text-[14px] text-[#9C9485]">{t.meta}</p>
                </div>
                <p className="hl-serif-italic text-[13px] text-[#E9C77A]">Verified · with permission</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/CTABanner.js': `import React from 'react';
import { ArrowIcon } from './Atmosphere';

export default function CTABanner() {
  return (
    <section className="relative overflow-hidden border-y border-[#E9C77A]/10 bg-[#0C0A08]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/2 h-[640px] w-[860px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E9C77A]/[0.06] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[1320px] px-8 py-32 md:px-14 lg:py-44">
        <div className="grid grid-cols-12 items-center gap-10">
          <div className="col-span-12 lg:col-span-7">
            <p className="hl-eyebrow"><span className="hl-rule" /> The folio</p>
            <h2 className="hl-serif mt-8 text-[56px] leading-[1.02] text-[#E9DFC5] sm:text-[80px] md:text-[108px] lg:text-[128px]">
              Find a home <br />
              <span className="hl-serif-italic text-[#E9C77A]">worth keeping.</span>
            </h2>
          </div>

          <div className="col-span-12 lg:col-span-5 lg:pl-10">
            <p className="hl-serif text-[20px] leading-[1.6] text-[#C2B698]">
              Tell us how you live, where you spend your seasons, and what
              the morning light should look like.
            </p>
            <p className="hl-serif mt-5 text-[20px] leading-[1.6] text-[#C2B698]">
              We send a private folio within forty-eight hours —
              <span className="hl-serif-italic"> no automation, no obligation,</span> just a letter
              from a real director.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <button type="button" className="hl-link text-[20px]">
                Request the folio
                <ArrowIcon size={14} />
              </button>
              <span className="hl-serif-italic text-[14px] text-[#9C9485]">or write to atelier@halcyon.studio</span>
            </div>
            <p className="hl-serif-italic mt-10 text-[13px] text-[#9C9485]">
              Discretion guaranteed. By appointment only.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Footer.js': `import React from 'react';
import { FOOTER_COLS, FOOTER_OFFICES, NAV_LINKS } from '../data/halcyon';

export default function Footer() {
  return (
    <footer className="relative bg-[#EFE5CB] text-[#1A1816]">
      <div className="mx-auto max-w-[1320px] px-8 py-24 md:px-14">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-6">
            <p className="hl-serif text-[96px] leading-[0.9] sm:text-[140px] md:text-[200px]">
              Halcyon<span className="hl-serif-italic text-[#9B6A2D]">.</span>
            </p>
            <p className="hl-serif-italic mt-6 text-[18px] text-[#6B5F4C]">
              An atelier of architecturally significant residences.
              Founded in Paris, MMXIV.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3">
              {FOOTER_COLS.map((col) => (
                <div key={col.title}>
                  <p className="hl-serif-italic text-[14px] text-[#6B5F4C]">{col.title}</p>
                  <ul className="mt-5 space-y-3">
                    {col.links.map((l) => (
                      <li key={l}>
                        <a href={'#' + l.toLowerCase().replace(/\\s/g, '-')} className="hl-serif text-[18px] text-[#1A1816] transition hover:text-[#9B6A2D]">{l}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 hl-divider-dark" />

        <div className="mt-10 grid grid-cols-12 gap-8">
          {FOOTER_OFFICES.map((o) => (
            <div key={o.city} className="col-span-12 sm:col-span-4">
              <p className="hl-serif-italic text-[13px] text-[#6B5F4C]">{o.city}</p>
              <p className="hl-serif mt-2 text-[24px] text-[#1A1816]">{o.addr}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 hl-divider-dark" />

        <div className="mt-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <p className="hl-serif-italic text-[13px] text-[#6B5F4C]">
            © MMXXVI Halcyon Atelier · All residences artistically represented.
          </p>
          <div className="flex flex-wrap items-center gap-8 hl-serif text-[15px] text-[#3A332B]">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="transition hover:text-[#9B6A2D]">{l.label}</a>
            ))}
            <a href="#privacy" className="hl-serif-italic text-[#6B5F4C]">Privacy &amp; terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
`,
};
